import React, { useState, useEffect } from 'react';
import { get, post, put } from '../utils/api';
import { useAuth } from '../context/AuthContext';

const STATUS_COLORS = {
  proposed:{ bg:'#F9F8F5', color:'#8A8A8A', label:'Proposed' },
  approved:{ bg:'#E8F5EB', color:'#2E6B3A', label:'Approved' },
  planning:{ bg:'#EDF3FF', color:'#3B5BDB', label:'Planning' },
  active:{ bg:'#D3F9D8', color:'#2B8A3E', label:'Active' },
  completed:{ bg:'#A8893D22', color:'#A8893D', label:'Completed' },
  cancelled:{ bg:'#F5F5F5', color:'#aaa', label:'Cancelled' },
};

export default function BrandCampaigns() {
  const { user } = useAuth();
  const canApprove = ['super_admin','sales_manager'].includes(user?.role);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name:'', objective:'', target_audience:'', channels:[],
    start_date:'', end_date:'', status:'proposed', notes:''
  });

  const CHANNELS = ['Instagram','TikTok','Facebook','Website','Blog','Email','WhatsApp'];

  useEffect(()=>{ load(); },[]);

  const load = () => {
    setLoading(true);
    get('/bc/campaigns').then(d=>setCampaigns(d.data||[])).finally(()=>setLoading(false));
  };

  const openForm = (item=null) => {
    if (item) {
      setEditing(item);
      setForm({ name:item.name, objective:item.objective||'',
        target_audience:item.target_audience||'', channels:item.channels||[],
        start_date:item.start_date?.split('T')[0]||'',
        end_date:item.end_date?.split('T')[0]||'',
        status:item.status, notes:item.notes||'' });
    } else {
      setEditing(null);
      setForm({name:'',objective:'',target_audience:'',channels:[],
        start_date:'',end_date:'',status:'proposed',notes:''});
    }
    setShowForm(true);
  };

  const save = async () => {
    if (!form.name) { alert('Campaign name required'); return; }
    setSaving(true);
    try {
      if (editing) await put('/bc/campaigns/'+editing.id, form);
      else await post('/bc/campaigns', form);
      setShowForm(false);
      load();
    } catch(err) { alert(err.message); }
    finally { setSaving(false); }
  };

  const toggleChannel = (ch) => {
    setForm(f=>({...f, channels: f.channels.includes(ch)
      ? f.channels.filter(c=>c!==ch) : [...f.channels, ch]}));
  };

  return (
    <div className="page">
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:20}}>
        <div>
          <h2 style={{fontFamily:'Cormorant Garamond,serif',fontSize:'1.8rem',color:'#2C1F16',margin:'0 0 4px'}}>Campaigns</h2>
          <p style={{color:'#8A8A8A',fontSize:13,margin:0}}>Plan and manage marketing campaigns</p>
        </div>
        <button className="btn btn-primary btn-sm" onClick={()=>openForm()}>+ New Campaign</button>
      </div>

      {loading ? <div className="spinner"/> : campaigns.length === 0 ? (
        <div className="empty-state"><h3>No campaigns yet</h3></div>
      ) : (
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(300px,1fr))',gap:14}}>
          {campaigns.map(c=>{
            const st = STATUS_COLORS[c.status]||STATUS_COLORS.proposed;
            const pct = c.content_count > 0 ? Math.round((c.published_count/c.content_count)*100) : 0;
            return (
              <div key={c.id} style={{background:'#fff',border:'1px solid #E8E0D4',
                borderRadius:12,padding:20}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'flex-start',marginBottom:10}}>
                  <div style={{fontSize:14,fontWeight:600,color:'#2C1F16',flex:1}}>{c.name}</div>
                  <span style={{fontSize:9,padding:'3px 10px',borderRadius:20,fontWeight:600,
                    background:st.bg,color:st.color,flexShrink:0,marginLeft:8}}>{st.label}</span>
                </div>
                {c.objective && <p style={{fontSize:12,color:'#555',marginBottom:10,lineHeight:1.5}}>{c.objective}</p>}
                <div style={{display:'flex',gap:12,fontSize:11,color:'#8A8A8A',marginBottom:10}}>
                  {c.start_date && <span>📅 {new Date(c.start_date).toLocaleDateString('en-NG',{day:'numeric',month:'short'})}</span>}
                  {c.end_date && <span>→ {new Date(c.end_date).toLocaleDateString('en-NG',{day:'numeric',month:'short'})}</span>}
                </div>
                <div style={{marginBottom:10}}>
                  <div style={{display:'flex',justifyContent:'space-between',fontSize:11,color:'#8A8A8A',marginBottom:4}}>
                    <span>Content Progress</span>
                    <span>{c.published_count}/{c.content_count} published</span>
                  </div>
                  <div style={{height:4,background:'#EDE7D8',borderRadius:2}}>
                    <div style={{width:pct+'%',height:4,background:'#556B2F',borderRadius:2}}/>
                  </div>
                </div>
                {c.channels?.length > 0 && (
                  <div style={{display:'flex',gap:4,flexWrap:'wrap',marginBottom:12}}>
                    {c.channels.map(ch=>(
                      <span key={ch} style={{fontSize:9,background:'#F2EFE7',color:'#556B2F',
                        padding:'2px 8px',borderRadius:10}}>{ch}</span>
                    ))}
                  </div>
                )}
                <button className="btn btn-outline btn-sm" style={{width:'100%'}}
                  onClick={()=>openForm(c)}>Edit Campaign</button>
              </div>
            );
          })}
        </div>
      )}

      {showForm && (
        <div className="modal-overlay">
          <div className="modal" style={{maxWidth:560}}>
            <div className="modal-hdr">
              <span className="modal-title">{editing?'Edit Campaign':'New Campaign'}</span>
              <button className="btn btn-ghost btn-sm" onClick={()=>setShowForm(false)}>X</button>
            </div>
            <div className="modal-body" style={{maxHeight:'75vh',overflowY:'auto'}}>
              <div className="form-group">
                <label className="form-label">Campaign Name *</label>
                <input className="form-control" value={form.name}
                  onChange={e=>setForm({...form,name:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Objective</label>
                <textarea className="form-control" rows={2} value={form.objective}
                  onChange={e=>setForm({...form,objective:e.target.value})} />
              </div>
              <div className="form-group">
                <label className="form-label">Target Audience</label>
                <input className="form-control" value={form.target_audience}
                  onChange={e=>setForm({...form,target_audience:e.target.value})} />
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Start Date</label>
                  <input type="date" className="form-control" value={form.start_date}
                    onChange={e=>setForm({...form,start_date:e.target.value})} />
                </div>
                <div className="form-group">
                  <label className="form-label">End Date</label>
                  <input type="date" className="form-control" value={form.end_date}
                    onChange={e=>setForm({...form,end_date:e.target.value})} />
                </div>
                {canApprove && (
                  <div className="form-group">
                    <label className="form-label">Status</label>
                    <select className="form-control" value={form.status}
                      onChange={e=>setForm({...form,status:e.target.value})}>
                      {Object.entries(STATUS_COLORS).map(([k,v])=>(
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>
              <div className="form-group">
                <label className="form-label">Channels</label>
                <div style={{display:'flex',gap:6,flexWrap:'wrap'}}>
                  {CHANNELS.map(ch=>(
                    <button key={ch} type="button" onClick={()=>toggleChannel(ch)}
                      style={{padding:'4px 12px',borderRadius:20,fontSize:11,cursor:'pointer',
                        border:`1px solid ${form.channels.includes(ch)?'#556B2F':'#ddd'}`,
                        background:form.channels.includes(ch)?'#556B2F':'#fff',
                        color:form.channels.includes(ch)?'#fff':'#555'}}>
                      {ch}
                    </button>
                  ))}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Notes</label>
                <textarea className="form-control" rows={2} value={form.notes}
                  onChange={e=>setForm({...form,notes:e.target.value})} />
              </div>
            </div>
            <div className="modal-ftr">
              <button className="btn btn-outline" onClick={()=>setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={save} disabled={saving}>
                {saving?'Saving...':editing?'Save Changes':'Create Campaign'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
